import React from "react";
import LoadingState from "../../components/basic/loading-state";
import PopoverMenu from "../../components/basic/popover-menu";
import Avatar from "../../components/basic/avatar";
import { resolveMediaUrl } from "../../helpers/media-url";
import type { AccountRow, Conversation, OutboundRow, ChatMessage } from "../../params/types";
import { formatRelative, igHandleProfileUrl } from "../shared";
import {
  GEAR_ICON,
  InlineIcon,
  formatMsgTime,
  isMediaPlaceholder,
  isVoiceMessage,
  sourceLabel,
  isScheduledBubble,
  IgProfileLink,
} from "./helpers";
import { ChatMedia, VoiceNoteBubble } from "./media";

export interface ThreadViewProps {
  accounts: AccountRow[];
  selectedAccount: string;
  selected: Conversation | null;
  newPeer: string;
  conversations: Conversation[];
  chatPeerName: string;
  threadFailed: OutboundRow[];
  busy?: boolean;
  displayedThreadPrompt: string;
  setThreadPrompt: (value: string) => void;
  setPromptDirty: (dirty: boolean) => void;
  promptDirty: boolean;
  savingPrompt: boolean;
  onToggleManual: () => void | Promise<void>;
  onSaveThreadPrompt: () => void | Promise<void>;
  onClearConversationHistory: () => void;
  onRescanFullHistory: () => void;
  onOpenAccountSettings?: ((account: AccountRow) => void) | null;
  onRetryOutbound: (id: string) => void | Promise<void>;
  onDiscardOutbound: (id: string) => void | Promise<void>;
  onOpenScheduledOutbound?: ((message: ChatMessage) => void) | null;
  chatMessagesRef: React.RefObject<HTMLDivElement | null>;
  chatContentRef: React.RefObject<HTMLDivElement | null>;
  onChatMessagesScroll?: () => void;
  loadingConvos?: boolean;
  loadingMessages?: boolean;
  children?: React.ReactNode;
}

export function ThreadView({
  accounts,
  selectedAccount,
  selected,
  newPeer,
  conversations,
  chatPeerName,
  threadFailed,
  busy,
  displayedThreadPrompt,
  setThreadPrompt,
  setPromptDirty,
  promptDirty,
  savingPrompt,
  onToggleManual,
  onSaveThreadPrompt,
  onClearConversationHistory,
  onRescanFullHistory,
  onOpenAccountSettings = null,
  onRetryOutbound,
  onDiscardOutbound,
  onOpenScheduledOutbound = null,
  chatMessagesRef,
  chatContentRef,
  onChatMessagesScroll,
  loadingConvos = false,
  loadingMessages = false,
  children,
}: ThreadViewProps) {
  return (
    <section className="ib-inbox-chat ib-chat">
      {!selected && !newPeer.trim() &&
        (loadingConvos ? (
          <LoadingState
            className="ib-chat__empty ib-empty--chat"
            label="Loading conversations…"
          />
        ) : (
          <div className="ib-chat__empty ib-empty ib-empty--chat">
            {conversations.length
              ? "Select a conversation"
              : "No conversations yet."}
          </div>
        ))}

      {(selected || newPeer.trim()) && (
        <>
          <div className="ib-chat__header ib-chat-header">
            <Avatar
              width={50}
              height={50}
              text={chatPeerName}
              colorful={true}
              image={resolveMediaUrl(selected?.avatar_url) || null}
            />
            <div className="ib-chat__header-info">
              <span className="ib-chat__username">{chatPeerName}</span>
              <IgProfileLink
                url={igHandleProfileUrl(
                  selected?.peer_username || newPeer.trim() || null,
                )}
              />
              <span className="ib-chat__fullname">
                {selected?.is_follower_only
                  ? "Follower · no DM yet"
                  : selectedAccount
                    ? `via ${selectedAccount}`
                    : ""}
              </span>
            </div>
            <div className="ib-chat__header-actions">
              {selected && !String(selected.thread_id).startsWith("peer:") && (
                <button
                  type="button"
                  className={`ib-takeover-toggle${
                    selected.manual_mode ? " ib-takeover-toggle--manual" : ""
                  }`}
                  disabled={busy}
                  title={
                    selected.manual_mode
                      ? "AI is paused for this chat — click to resume"
                      : "Pause AI and reply manually"
                  }
                  onClick={() => onToggleManual()}
                >
                  <span className="ib-takeover-toggle__indicator" />
                  <span className="ib-takeover-toggle__label">
                    {selected.manual_mode ? "Resume AI" : "Take over"}
                  </span>
                </button>
              )}
              {selected && (
                <PopoverMenu
                  placement="bottom-end"
                  className="ib-contact-settings-trigger-wrap"
                  menu={(close) => {
                    const canManageThread =
                      selected &&
                      !String(selected.thread_id).startsWith("peer:");
                    return (
                      <div
                        className="ib-contact-settings"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <div className="ib-contact-settings__head">
                          <div className="ib-contact-settings__title">
                            Conversation
                          </div>
                          <div className="ib-contact-settings__subtitle">
                            @{chatPeerName}
                            {selectedAccount ? ` · ${selectedAccount}` : ""}
                            {selected.manual_mode ? " · manual" : ""}
                          </div>
                        </div>

                        {canManageThread ? (
                          <div className="ib-contact-settings__prompt">
                            <label
                              htmlFor="ib-thread-ai-prompt"
                              className="ib-contact-settings__label"
                            >
                              Thread AI prompt
                            </label>
                            <p className="ib-contact-settings__hint">
                              Optional override for this chat only. Leave empty
                              to use the account prompt.
                            </p>
                            <textarea
                              id="ib-thread-ai-prompt"
                              rows={3}
                              value={displayedThreadPrompt}
                              placeholder="Leave empty for the account reply prompt"
                              onChange={(e) => {
                                setThreadPrompt(e.target.value);
                                setPromptDirty(true);
                              }}
                            />
                            <button
                              type="button"
                              className="jf-btn jf-btn-primary jf-btn-sm"
                              disabled={busy || savingPrompt || !promptDirty}
                              onClick={async () => {
                                await onSaveThreadPrompt();
                              }}
                            >
                              {savingPrompt ? "Saving…" : "Save prompt"}
                            </button>
                          </div>
                        ) : (
                          <div className="ib-muted ib-contact-settings__hint">
                            Sync this thread from Instagram to edit the
                            per-thread AI prompt or clear history.
                          </div>
                        )}

                        <div className="ib-contact-settings__actions">
                          {canManageThread && (
                            <button
                              type="button"
                              className="ib-contact-settings__action"
                              disabled={busy || !selectedAccount}
                              onClick={() => {
                                close();
                                onRescanFullHistory();
                              }}
                            >
                              Rescan full history
                            </button>
                          )}
                          {canManageThread && (
                            <button
                              type="button"
                              className="ib-contact-settings__action ib-contact-settings__action--danger"
                              disabled={busy || !selectedAccount}
                              onClick={() => {
                                close();
                                onClearConversationHistory();
                              }}
                            >
                              Clear history
                            </button>
                          )}
                          <button
                            type="button"
                            className="ib-contact-settings__action"
                            disabled={
                              !selectedAccount || !onOpenAccountSettings
                            }
                            onClick={() => {
                              close();
                              const acc = accounts.find(
                                (a) => a.username === selectedAccount,
                              );
                              if (acc) onOpenAccountSettings?.(acc);
                            }}
                          >
                            Account settings
                          </button>
                        </div>
                      </div>
                    );
                  }}
                >
                  <button
                    type="button"
                    className="ib-contact-settings-btn"
                    title="Conversation settings"
                    aria-label="Conversation settings"
                  >
                    <InlineIcon d={GEAR_ICON} size={18} />
                  </button>
                </PopoverMenu>
              )}
            </div>
          </div>

          {threadFailed.length > 0 && (
            <div className="ib-failed-outbound">
              <div className="ib-failed-outbound__title">Failed outbound</div>
              {threadFailed.map((o) => (
                <div key={o.id} className="ib-failed-outbound__row">
                  <div className="ib-failed-outbound__body">
                    <div className="ib-failed-outbound__text">{o.text}</div>
                    <div className="ib-muted">
                      {o.error || "Send failed"}
                      {o.created_at ? ` · ${formatRelative(o.created_at)}` : ""}
                    </div>
                  </div>
                  <div className="ib-failed-outbound__actions">
                    <button
                      type="button"
                      className="ib-link"
                      disabled={busy}
                      onClick={() => onRetryOutbound(o.id)}
                    >
                      Retry
                    </button>
                    <button
                      type="button"
                      className="ib-link ib-link--danger"
                      disabled={busy}
                      onClick={() => onDiscardOutbound(o.id)}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div
            className="ib-chat__messages ib-chat-messages"
            ref={chatMessagesRef}
            onScroll={onChatMessagesScroll}
          >
            <div ref={chatContentRef}>
              {(selected?.messages || []).map((m) => {
                if (m.from === "system") {
                  return (
                    <div key={m.id} className="ib-chat-msg ib-chat-msg--system">
                      <div className="ib-bubble ib-bubble--system">
                        <div className="ib-bubble__text">{m.text}</div>
                      </div>
                    </div>
                  );
                }
                const isVoice = isVoiceMessage({
                  text: m.text,
                  mediaKind: m.media_kind,
                });
                const hasMedia = Boolean(m.media_url);
                const showText =
                  m.text &&
                  (!isMediaPlaceholder(m.text) ||
                    (!hasMedia && !isVoice && Boolean(m.media_kind)));
                const scheduled = isScheduledBubble(m.source);
                const isOutbound =
                  m.from === "me" ||
                  m.source === "queued" ||
                  m.source === "sending" ||
                  scheduled;
                const bubbleMod =
                  scheduled
                    ? "scheduled"
                    : m.source === "queued" || m.source === "sending"
                      ? "queued"
                      : m.source === "admin"
                        ? "admin"
                        : m.from === "me"
                          ? "me"
                          : "them";
                const openScheduled =
                  scheduled && m.outbound_id && onOpenScheduledOutbound
                    ? () => onOpenScheduledOutbound(m)
                    : undefined;
                return (
                  <div
                    key={m.id}
                    className={`ib-chat-msg${
                      isOutbound ? " ib-chat-msg--out" : " ib-chat-msg--in"
                    }`}
                  >
                    {!isOutbound && (
                      <Avatar
                        width={35}
                        height={35}
                        text={chatPeerName}
                        colorful={true}
                      />
                    )}
                    <div className="ib-chat-msg__wrap">
                      <div
                        className={`ib-chat-msg__bubble ib-bubble ib-bubble--${bubbleMod}${
                          openScheduled ? " ib-bubble--clickable" : ""
                        }`}
                        role={openScheduled ? "button" : undefined}
                        tabIndex={openScheduled ? 0 : undefined}
                        onClick={openScheduled}
                        onKeyDown={
                          openScheduled
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openScheduled();
                                }
                              }
                            : undefined
                        }
                      >
                        {isVoice ? (
                          hasMedia ? (
                            <ChatMedia
                              key={m.media_url || m.id}
                              mediaUrl={m.media_url}
                              mediaKind={m.media_kind || "voice"}
                              alt={m.media_name || "Voice note"}
                            />
                          ) : (
                            <VoiceNoteBubble />
                          )
                        ) : hasMedia ? (
                          <ChatMedia
                            key={m.media_url || m.id}
                            mediaUrl={m.media_url}
                            mediaKind={m.media_kind}
                            alt={m.media_name || "Attachment"}
                          />
                        ) : null}
                        {showText ? (
                          <div className="ib-bubble__text">{m.text}</div>
                        ) : null}
                        {!hasMedia && m.media_name ? (
                          <div className="ib-bubble__meta">
                            📎 {m.media_name}
                          </div>
                        ) : null}
                      </div>
                      <div
                        className={`ib-chat-msg__ts${
                          isOutbound ? " ib-chat-msg__ts--right" : ""
                        }`}
                      >
                        {formatMsgTime(m.at)}
                        {sourceLabel(m.source)}
                      </div>
                    </div>
                  </div>
                );
              })}
              {loadingMessages && !selected?.messages?.length && (
                <LoadingState label="Loading messages…" />
              )}
              {!loadingMessages && !selected?.messages?.length && (
                <div className="ib-empty">
                  {selected?.is_follower_only
                    ? "No messages yet — send a welcome or start the conversation."
                    : "No messages yet."}
                </div>
              )}
            </div>
          </div>

          {children}
        </>
      )}
    </section>
  );
}
